import { useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { parseIdeaRoomExportJson, translateIdeaRoomExport } from "@/lib/ops/ideaRoomExport";
import { getOpsSettings } from "@/lib/opsPaperworkClient";
import { ideaRoomWebhookPublicPath, publicAppOrigin } from "@/lib/publicAppOrigin";
import { ghostBtnClass, inputClass, primaryBtnClass } from "./ManufacturingOrderIntakeShared";

export function ManufacturingOrderIntakeIdeaRoomImport(props: {
  busy: boolean;
  error: string | null;
  onSubmit: (payload: unknown) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await getOpsSettings();
      if (cancelled || !r.ok) return;
      const token = String(
        (r.data.settings as { inventory_host_inbound_webhook_token?: unknown })
          .inventory_host_inbound_webhook_token ?? ""
      ).trim();
      if (!token) return;
      const path = ideaRoomWebhookPublicPath(token);
      const origin = publicAppOrigin();
      setWebhookUrl(origin ? `${origin}${path}` : path);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const preview = (() => {
    const parsed = parseIdeaRoomExportJson(text);
    if (!parsed.ok) return null;
    const t = translateIdeaRoomExport(parsed.payload);
    return t.ok ? t.translation : null;
  })();

  async function submit() {
    setFormError(null);
    const parsed = parseIdeaRoomExportJson(text);
    if (!parsed.ok) {
      setFormError(parsed.error);
      return;
    }
    const t = translateIdeaRoomExport(parsed.payload);
    if (!t.ok) {
      setFormError(t.error);
      return;
    }
    await props.onSubmit(parsed.payload);
  }

  return (
    <SectionCard title="Import IdeaRoom JSON">
      <p className="text-xs text-gray-600 dark:text-zinc-400">
        Paste the IdeaRoom order/quote JSON so it lands in Pending Review without going through
        ShedSuite. Same-id exports merge into an existing on-hold row.
      </p>
      {webhookUrl ? (
        <label className="mt-2 block text-xs text-gray-600">
          Webhook URL (same org token as ShedSuite inbound)
          <input className={`${inputClass} mt-0.5`} readOnly value={webhookUrl} />
        </label>
      ) : (
        <p className="mt-2 text-xs text-gray-500">
          Direct POST URL is on Operations → Settings after you generate the inbound webhook token.
        </p>
      )}
      <textarea
        className={`${inputClass} mt-2 min-h-[10rem] font-mono text-xs`}
        value={text}
        disabled={props.busy}
        onChange={(e) => setText(e.target.value)}
        placeholder='{"eventType":"created","hash":"…","order":{"customer":{"firstName":"…"}}}'
      />
      {preview ? (
        <p className="mt-2 text-xs text-gray-600">
          Will import <span className="font-medium">{preview.external_source_id}</span>
          {preview.customer_display_name ? ` · ${preview.customer_display_name}` : ""} · {preview.kind}
        </p>
      ) : null}
      {formError || props.error ? (
        <p className="mt-2 text-sm text-rose-800">{formError || props.error}</p>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={primaryBtnClass} disabled={props.busy} onClick={() => void submit()}>
          Import to review queue
        </button>
        <button type="button" className={ghostBtnClass} disabled={props.busy} onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </SectionCard>
  );
}
